import ResourceManager from "../../components/ResourceManager"
import { StringMap } from "../../types/common"
import { useI18n } from "../../hooks"
import { useOptions } from "../../options/hooks/use-options"
import { TaskViewModel } from "../api/task"
import { useTaskMutations } from "../hooks/use-task-mutations"

const TaskParticipantsWidget = ({
  task,
  boardId,
  columnPosition,
}: {
  task: TaskViewModel
  boardId?: string
  columnPosition?: number
}) => {
  const translations = useTranslations(defaultTranslations)
  const { membershipOptions } = useOptions(task.maintainerId)
  const { addParticipants, removeParticipants } = useTaskMutations()
  const isNotManager = ({ id }: { id: string }) => task.managers.findIndex((m) => m.id === id) < 0
  const handleAddParticipants = (ids: string[]) => {
    const participants = membershipOptions
      .filter((member) => ids.includes(member.id))
      .map((member) => ({ name: member.name, id: member.id }))
    addParticipants(task.id, participants, boardId, columnPosition)
  }

  const handleRemoveParticipants = (id: string) => {
    const participants = task.participants
      .filter((member) => member.id === id)
      .map((member) => ({ name: member.name, id: member.id }))
    removeParticipants(task.id, { participants, boardId })
  }

  const sortedMembershipOptions = membershipOptions
    .filter(isNotManager)
    .slice()
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
  const sortedTaskParticipants = task.participants
    .slice()
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))

  return (
    <ResourceManager
      title={translations.title}
      resourceOptions={sortedMembershipOptions}
      resources={sortedTaskParticipants}
      onAddResources={handleAddParticipants}
      onRemoveResource={handleRemoveParticipants}
      canAddResource={task.canUpdateParticipants}
      canRemoveResource={task.canUpdateParticipants}
      emptyOptionsMessage={translations.emptyOptionsMessage}
      emptyResourcesMessage={translations.emptyResourcesMessage}
      isMultipleSelection
    />
  )
}

const useTranslations = (defaults: Translations = defaultTranslations): Translations => {
  const { translations: t } = useI18n("task")
  const translations = (t?.participantsWidget || {}) as StringMap
  const {
    title = defaults.title,
    emptyOptionsMessage = defaults.emptyOptionsMessage,
    emptyResourcesMessage = defaults.emptyResourcesMessage,
  } = translations

  return {
    title,
    emptyOptionsMessage,
    emptyResourcesMessage,
  }
}

const defaultTranslations = {
  title: "Participants",
  emptyOptionsMessage: "No members in this organisation",
  emptyResourcesMessage: "No participant in this task",
}

type Translations = typeof defaultTranslations

export default TaskParticipantsWidget
